/**
 * DNACard - Animated Palm DNA Trait Card Component
 * Phase 3.3 - Premium DNA visualization with reveal animations
 * 
 * Key principles:
 * - Staggered reveal animation
 * - Flip card effect (front: icon+trait, back: insight)
 * - Observation-backed indicator
 * - Confidence badge
 * - Premium, calm aesthetic
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import { 
  Heart, Briefcase, Crown, Eye, Palette, 
  Target, Users, MessageCircle, Globe, Flame,
  Check, Info, ChevronRight
} from 'lucide-react';

// Trait icons using Lucide (replacing emojis)
const TRAIT_ICONS = {
  love: Heart,
  career: Briefcase,
  leadership: Crown,
  intuition: Eye,
  creativity: Palette,
  discipline: Target,
  empathy: Users,
  communication: MessageCircle,
  adventure: Globe,
  confidence: Flame
};

// Trait colors for visual distinction
const TRAIT_COLORS = {
  love: { bg: 'rgba(239, 68, 68, 0.1)', border: '#ef4444', text: '#dc2626' },
  career: { bg: 'rgba(59, 130, 246, 0.1)', border: '#3b82f6', text: '#2563eb' },
  leadership: { bg: 'rgba(245, 158, 11, 0.1)', border: '#f59e0b', text: '#d97706' },
  intuition: { bg: 'rgba(168, 85, 247, 0.1)', border: '#a855f7', text: '#9333ea' },
  creativity: { bg: 'rgba(236, 72, 153, 0.1)', border: '#ec4899', text: '#db2777' },
  discipline: { bg: 'rgba(34, 197, 94, 0.1)', border: '#22c55e', text: '#16a34a' },
  empathy: { bg: 'rgba(20, 184, 166, 0.1)', border: '#14b8a6', text: '#0d9488' },
  communication: { bg: 'rgba(99, 102, 241, 0.1)', border: '#6366f1', text: '#4f46e5' },
  adventure: { bg: 'rgba(249, 115, 22, 0.1)', border: '#f97316', text: '#ea580c' },
  confidence: { bg: 'rgba(239, 68, 68, 0.1)', border: '#ef4444', text: '#dc2626' }
};

// Trait names for localization
const TRAIT_NAMES = {
  love: { tr: 'Sevgi', en: 'Love' },
  career: { tr: 'Kariyer', en: 'Career' },
  leadership: { tr: 'Liderlik', en: 'Leadership' },
  intuition: { tr: 'Sezgi', en: 'Intuition' },
  creativity: { tr: 'Yaratıcılık', en: 'Creativity' },
  discipline: { tr: 'Disiplin', en: 'Discipline' },
  empathy: { tr: 'Empati', en: 'Empathy' },
  communication: { tr: 'İletişim', en: 'Communication' },
  adventure: { tr: 'Macera', en: 'Adventure' },
  confidence: { tr: 'Özgüven', en: 'Confidence' }
};

// Confidence level labels
const CONFIDENCE_LABELS = {
  high: { tr: 'Yüksek Güven', en: 'High Confidence' },
  medium: { tr: 'Orta Güven', en: 'Medium Confidence' },
  low: { tr: 'Düşük Güven', en: 'Low Confidence' }
};

/**
 * Get confidence level from score
 */
const getConfidenceLevel = (confidence) => {
  if (confidence >= 0.7) return 'high';
  if (confidence >= 0.4) return 'medium';
  return 'low';
};

/**
 * Single DNA Card Component
 */
const DNACard = ({
  trait,
  score,
  confidence,
  explanation,
  sourceFeatures = [],
  index = 0,
  lang = 'tr',
  isStrongest = false,
  onExpand
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const TraitIcon = TRAIT_ICONS[trait] || Heart;
  const colors = TRAIT_COLORS[trait] || TRAIT_COLORS.love;
  const traitName = TRAIT_NAMES[trait]?.[lang] || trait;
  const confidenceLevel = getConfidenceLevel(confidence);
  const confidenceLabel = CONFIDENCE_LABELS[confidenceLevel]?.[lang];
  
  const hasObservationBacking = sourceFeatures && sourceFeatures.length > 0;
  
  // Animation variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: index * 0.15,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  
  const flipVariants = {
    front: { rotateY: 0 },
    back: { rotateY: 180 }
  };
  
  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };
  
  const handleExpand = () => {
    setIsExpanded(!isExpanded);
    if (onExpand) onExpand(trait);
  };
  
  return (
    <motion.div
      className={`dna-card ${isStrongest ? 'dna-card-strongest' : ''} ${isFlipped ? 'dna-card-flipped' : ''}`}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      data-testid={`dna-card-${trait}`}
      data-trait={trait}
      style={{
        '--card-bg': colors.bg,
        '--card-border': colors.border,
        '--card-text': colors.text
      }}
    >
      {/* Card Inner - for flip effect */}
      <div 
        className="dna-card-inner"
        onClick={handleFlip}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleFlip()}
        aria-label={`${traitName} - ${score}%`}
      >
        {/* Front Face */}
        <motion.div 
          className="dna-card-face dna-card-front"
          animate={isFlipped ? 'back' : 'front'}
          variants={flipVariants}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          {/* Strongest badge */}
          {isStrongest && (
            <div className="dna-strongest-badge">
              <Crown size={12} />
              <span>{lang === 'tr' ? 'En Güçlü' : 'Strongest'}</span>
            </div>
          )}
          
          {/* Icon */}
          <div className="dna-card-icon" style={{ color: colors.text }}>
            <TraitIcon size={28} strokeWidth={1.5} />
          </div>
          
          {/* Trait Name */}
          <h4 className="dna-card-trait">{traitName}</h4>
          
          {/* Score Bar */}
          <div className="dna-card-score-container">
            <motion.div 
              className="dna-card-score-bar"
              initial={{ width: 0 }}
              animate={{ width: `${score}%` }}
              transition={{ 
                duration: 1, 
                delay: index * 0.15 + 0.3,
                ease: 'easeOut'
              }}
              style={{ backgroundColor: colors.border }}
            />
            <span className="dna-card-score-value">{score}%</span>
          </div>
          
          {/* Confidence Badge */}
          <div className={`dna-confidence-badge dna-confidence-${confidenceLevel}`}>
            <span className="confidence-dot" />
            <span>{confidenceLabel}</span>
          </div>
          
          {/* Observation-backed indicator */}
          {hasObservationBacking && (
            <div className="dna-observation-badge" title={lang === 'tr' ? 'Gözleme dayalı' : 'Observation-backed'}>
              <Check size={12} />
              <span>{lang === 'tr' ? 'Gözleme Dayalı' : 'Observation-backed'}</span>
            </div>
          )}
          
          {/* Flip hint */}
          <div className="dna-card-flip-hint">
            <Info size={14} />
            <span>{lang === 'tr' ? 'Detay için tıkla' : 'Tap for details'}</span>
          </div>
        </motion.div>
        
        {/* Back Face */}
        <motion.div 
          className="dna-card-face dna-card-back"
          animate={isFlipped ? 'front' : 'back'}
          variants={flipVariants}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <div className="dna-card-back-header">
            <TraitIcon size={20} strokeWidth={1.5} style={{ color: colors.text }} />
            <h4>{traitName}</h4>
          </div>
          
          {/* Insight/Explanation */}
          <p className="dna-card-insight">
            {explanation || (lang === 'tr' 
              ? 'Bu özellik avuç içi gözlemlerinize dayanmaktadır.'
              : 'This trait is based on your palm observations.'
            )}
          </p>
          
          {/* Source Features (if available) */}
          {hasObservationBacking && (
            <div className="dna-card-sources">
              <div className="sources-header">
                <Eye size={14} />
                <span>{lang === 'tr' ? 'Dayanak Gözlemler' : 'Source Observations'}</span>
              </div>
              <ul className="sources-list">
                {sourceFeatures.slice(0, 3).map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Back to front hint */}
          <div className="dna-card-flip-hint">
            <ChevronRight size={14} />
            <span>{lang === 'tr' ? 'Geri dön' : 'Go back'}</span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

DNACard.propTypes = {
  trait: PropTypes.string.isRequired,
  score: PropTypes.number.isRequired,
  confidence: PropTypes.number,
  explanation: PropTypes.string,
  sourceFeatures: PropTypes.arrayOf(PropTypes.string),
  index: PropTypes.number,
  lang: PropTypes.string,
  isStrongest: PropTypes.bool,
  onExpand: PropTypes.func
};

/**
 * DNA Cards Grid - Container for all trait cards
 */
export const DNACardsGrid = ({
  traits = [],
  strongestTrait,
  lang = 'tr',
  className = ''
}) => {
  // Sort traits by score, strongest first
  const sortedTraits = [...traits].sort((a, b) => b.score - a.score);
  
  return (
    <motion.div 
      className={`dna-cards-grid ${className}`}
      initial="hidden"
      animate="visible"
      data-testid="dna-cards-grid"
    >
      {sortedTraits.map((trait, index) => (
        <DNACard
          key={trait.trait}
          trait={trait.trait}
          score={trait.score}
          confidence={trait.confidence}
          explanation={trait[`explanation_${lang}`] || trait.explanation_tr}
          sourceFeatures={trait.source_features}
          index={index}
          lang={lang}
          isStrongest={trait.trait === strongestTrait}
        />
      ))}
    </motion.div>
  );
};

DNACardsGrid.propTypes = {
  traits: PropTypes.arrayOf(PropTypes.shape({
    trait: PropTypes.string.isRequired,
    score: PropTypes.number.isRequired,
    confidence: PropTypes.number,
    source_features: PropTypes.arrayOf(PropTypes.string),
    explanation_tr: PropTypes.string,
    explanation_en: PropTypes.string
  })),
  strongestTrait: PropTypes.string,
  lang: PropTypes.string,
  className: PropTypes.string
};

export default DNACard;
